import httpClient from "@/httpClient"
import { createCancellable } from "@/utils/cancellable"
import { CANCEL_SOURCES } from "@/constants/CANCEL_SOURCES"

let getRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
let getKanbanRecordsCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD)
let getCardRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)

const getRecords = (filters, dropPrevious = false, config = null) => {
  if (dropPrevious) {
    getRecordsCancellable.abort()
    getRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/all',
    filters,
    config || getRecordsCancellable.config,
  )
}

// Kanban board columns. All three entry points share getKanbanRecordsCancellable,
// which wraps a single AbortController, so calling with dropPrevious: true (e.g. a
// full board reload) aborts any in-flight per-column "load more" — intentional, since
// a filter change should supersede a load-more.
const getKanbanRecords = (payload, dropPrevious = false) => {
  if (dropPrevious) {
    getKanbanRecordsCancellable.abort()
    getKanbanRecordsCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/kanban',
    payload,
    getKanbanRecordsCancellable.config,
  )
}

const getCardRecords = (payload, dropPrevious = false) => {
  if (dropPrevious) {
    getCardRecordsCancellable.abort()
    getCardRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/cards',
    payload,
    getCardRecordsCancellable.config,
  )
}

const exportRecords = (filters) => httpClient.post(
  '{currentCompanySlug}/quotes/export',
  filters,
)

const createRecord = (company, payload, tryCount = 0) => {
  return httpClient({
    method: 'POST',
    url: `${company || '{currentCompanySlug}'}/quotes`,
    data: payload,
    headers: {
      notification: 'silent',
    },
    hideValidationSections: true,
  }).catch((e) => {
    if (e?.code === "ERR_NETWORK" && tryCount < 4) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          createRecord(company, payload, tryCount + 1)
            .then(resolve)
            .catch(reject)
        }, 5000)
      })
    }

    throw e
  })
}

export const recordVAIParse = (payload, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'POST',
    url: `${import.meta.env.VITE_AI_EXTRACT_BASE_URL}/${company || '{currentCompanySlug}'}/vsimple-ai/extract-document-fields`,
    data: payload,
  })
}

export const recordVAIParseExistingRecord = (payload, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'POST',
    url: `${import.meta.env.VITE_AI_EXTRACT_BASE_URL}/${company || '{currentCompanySlug}'}/vsimple-ai/extract-file-fields`,
    data: payload,
  })
}

const editRecord = ({ id, payload }, company = '{currentCompanySlug}', ignoreEventBus = false) => httpClient.patch(`${company}/quotes/${id}`, payload, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

const deleteRecord = ({ id }, company = '{currentCompanySlug}') => httpClient.delete(`${company}/quotes/${id}`)

const getRecord = (id, params, company = '{currentCompanySlug}', config = {}) => httpClient.get(`${company}/quotes/${id}`, { params, ...config })

export const readRecordStatuses = (company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/statuses`)

const getRecordActivityStream = (id, params, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/activity-stream`, {
  params,
  ignoreGlobalNotFoundCatch: true,
})

export const getRecordActivityStreamGroup = (recordId, postGroupId, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${recordId}/activity-stream/${postGroupId}`, {
  ignoreGlobalNotFoundCatch: true,
})

const getRecordFiles = ({ id, group }, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/file-attachment`, {
  params: { group },
  ignoreGlobalNotFoundCatch: true,
})

const getRecordFileGroups = (id, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/files-groups`, {
  ignoreGlobalNotFoundCatch: true,
})

const createMessage = ({ id, payload }, email, tryCount = 0, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'PUT',
    url: `${company}/quotes/${id}/activity-stream/messages`,
    data: payload,
    skipResponseInterceptor: tryCount < 4,
  }).catch((e) => {
    if (e?.code === "ERR_NETWORK" && tryCount < 4) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          createMessage({ id, payload }, email, tryCount + 1, company)
            .then(resolve)
            .catch(reject)
        }, 5000)
      })
    }

    throw e
  })
}

const updateMessage = ({ id, uuid, data }, company = '{currentCompanySlug}') => httpClient.put(
    `${company}/quotes/${id}/activity-stream/messages/${uuid}`,
  {
    ...data,
  },
)

const updateRecordStatus = ({ id, status, fields }, ignoreEventBus = false, company = '{currentCompanySlug}') => httpClient.patch(`${company}/quotes/${id}`, { status, fields }, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

const updateRecordType = ({ id, recordType, fields }, ignoreEventBus = false) => httpClient.patch(`{currentCompanySlug}/quotes/${id}`, { recordType, fields }, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

export const getFiltersStructure = (forceCompany = null) =>
  httpClient.get(`${forceCompany || '{currentCompanySlug}'}/quotes/filters`)

const addCollaborators = ({ id, accountIds = [] }, company = '{currentCompanySlug}') => httpClient({
  method: 'POST',
  url: `${company}/records/${id}/collaborators/add`,
  data: { accountIds },
  headers: { 'Content-Type': 'application/json', notification: 'silent' },
})

const removeCollaborators = ({ id, accountIds }, company = '{currentCompanySlug}') =>
  httpClient.post(`${company}/records/${id}/collaborators/remove`,{ accountIds } )

const moveChildRecord = ({ fromRecordId, objectId, toRecordId }, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${fromRecordId}/customFields/objects/${objectId}/move`,
  { recordId: toRecordId },
)

const moveChildRecordMultiple = ({ fromRecordId, objectIds, toRecordId }, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${fromRecordId}/customFields/objects/mass-move`,
  {
    recordId: toRecordId,
    objectIds,
  },
)
const deleteObjectChild = ({ objectId, data }, company = '{currentCompanySlug}') => httpClient.delete(
  `${company}/quotes/customFields/${objectId}/objects`,
  {
    data,
  },
)

const editObjectChild = ({ objectId, fieldId, data }) => httpClient.put(
  `{currentCompanySlug}/quotes/customFields/${objectId}/objects/${fieldId}`,
  {
    data,
  },
)

const createObjectChilds = ({ customFieldSlug, recordId, data }) => httpClient.post(
  `{currentCompanySlug}/quotes/${recordId}/customFields/${customFieldSlug}/mass-create`,
  {
    data,
  },
)

const getObjectChildren = ({ objectId }, company = '{currentCompanySlug}') => httpClient.get(
  `${company}/quotes/customFields/${objectId}/objects`,
)

const getObjectChildrenMeta = ({ objectId }, company = '{currentCompanySlug}') => httpClient.get(
  `${company}/quotes/customFields/${objectId}/objects/meta`,
)

const patchObjectField = ({ data, recordId }, company = '{currentCompanySlug}') => httpClient.put(
  `${company}/quotes/customFields/objects`,
  {
    ...data,
  },
  {
    validationRecordId: recordId || null,
  },
)

const patchObjectFieldDryRun = ({ data }, company = '{currentCompanySlug}') => httpClient.put(
  `${company}/quotes/customFields/objects?dry-run`,
  {
    data,
  },
  {
    ignoreGlobalNotify: true,
  },
)

const sortObjectFiedlds = ({ recordId, objectSlug, data }, company = '{currentCompanySlug}') => httpClient.put(
  `${company}/quotes/${recordId}/customFields/${objectSlug}/objects/sort`,
  {
    ...data,
  },
)

const templateDocumentsList = (company = '{currentCompanySlug}') => httpClient.get(
  `${company}/supplierCompany/template-documents`,
)

const getPersistentColumns = () => httpClient.get(
  '{currentCompanySlug}/quotes/persistent-columns',
)

const savePersistentColumns = (columns) => httpClient.post(
  '{currentCompanySlug}/quotes/persistent-columns',
  {
    columns,
  },
)

const deleteFileFromRecord = (payload, company = '{currentCompanySlug}') => httpClient.delete(
  `${company}/quotes/${payload.recordId}/file-attachment/${payload.fileId}`,
)

const restoreRecordFile = (payload, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/recycle-bin/${payload.recordId}/file/${payload.fileId}`,
)

export const loadRecordCustomFieldsConfiguration = (forceCompany = null, params = {}) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/records/customFields/configurations`,
  {
    params,
  },
)

export const loadRecordSections = (forceCompany = null) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/records/customFields/sections`,
)

const createStatus = (payload) => httpClient.post('{currentCompanySlug}/quotes/statuses', payload)

const changeStatusesOrder = (payload) => httpClient.post('{currentCompanySlug}/quotes/statuses/reorder', payload)

const deleteStatus = ({ statusId, params }) => httpClient.delete(
  `{currentCompanySlug}/quotes/statuses/${statusId}`,
  {
    params,
  },
)

const updateStatus = (payload) => httpClient.put(`{currentCompanySlug}/quotes/statuses/${payload.id}`, payload)

const loadFiltersViews = () => httpClient.get('{currentCompanySlug}/filter-views/quotes')

export const loadFiltersViewsForCompany = (company) =>
  httpClient.get(`${company}/filter-views/quotes`)

export const loadAllUnfavoriteCompanyFiltersViews = (company) =>
  httpClient.get(`${company}/filter-views?favourite=0`)

export const updateFavoriteViewForCompany = (payload, company) =>
  httpClient.post(`${company}/filter-views/quotes/favourite`, { data: payload })

export const getRecordsForCompany = (company, filters) =>
  httpClient.post(`${company}/quotes/all`, filters)

export const loadFiltersViewsCounts = (ids, forceCompany = null) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/filter-views/quotes/counts`,
  {
    params: { ids },
  },
)

const createFiltersView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes', payload)

const reorderFiltersView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/reorder', payload)

const deleteFiltersView = (filterViewId) => httpClient.delete(`{currentCompanySlug}/filter-views/quotes/${filterViewId}`)

const updateFiltersView = (payload) => httpClient.patch(
  `{currentCompanySlug}/filter-views/quotes/${payload.filterViewId}`, payload.data,
)

const updateFiltersViewHidden = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/hide', { data: payload })

const updateFavoriteView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/favourite', { data: payload })

const getRecordCollaborators = (payload, company = '{currentCompanySlug}') => httpClient.get(`${company}/records/${payload.recordId}/collaborators`, {
  ignoreGlobalNotFoundCatch: true,
})

const signTemplate = ({ orderId, templateId, payload, company = '{currentCompanySlug}' }) => httpClient.post(
  `${company}/quotes/${orderId}/template-document/${templateId}/e-sign`,
  payload,
)
const createEmbedLinkToSignTemplate = ({ orderId, templateId, payload, company = '{currentCompanySlug}' }) => {
  return httpClient.post(
    `${company}/quotes/${orderId}/template-document/${templateId}/e-sign/createTemplateEmbedLink`,
    payload,
  )
}

export const loadCustomFieldChangeLog = ({ recordId, customFieldSlug }, page, company = '{currentCompanySlug}') => {
  return httpClient.get(`${company}/records/${recordId}/change-log/${customFieldSlug}`, {
    params: {
      page,
    },
  })
}

export const getSharedTableData = (recordId, linkSlug, childObjectSlug, company = '{currentCompanySlug}') => {
  return httpClient.get(`/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}`)
}

export const reorderSharedTable = (recordId, linkSlug, childObjectSlug, linkedRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.put(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/${linkedRecordId}/reorder`,
    payload,
  )
}

export const sortSharedTable = (recordId, linkSlug, childObjectSlug, linkedRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.put(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/${linkedRecordId}`,
    payload,
  )
}
export const updateSharedTableData = (recordId, linkSlug, payload, company = '{currentCompanySlug}') => {
  return httpClient.patch(`/${company}/records/${recordId}/shared-tables/${linkSlug}`, payload, {
    skipDefaultForbiddenBehaviour: true,
    ignoreGlobalNotify: true,
  })
}

export const getManyToManyLinkData = (customFiledId, params, company = '{currentCompanySlug}') => {
  return httpClient.get(`/${company}/quotes/customFields/${customFiledId}/linkedRecords`, {
    params: {
      per_page: 20,
      ...params,
    },
    ignoreGlobalNotFoundCatch: true,
  })
}
export const getManyToManyAllLinkedRecords = (customFiledId, company = '{currentCompanySlug}') => {
  return httpClient.get(`/${company}/quotes/customFields/${customFiledId}/allLinkedRecords`)
}

export const deleteManyToManyLink = (customFiledId, recordId, company = '{currentCompanySlug}') => {
  return httpClient.delete(`/${company}/quotes/customFields/${customFiledId}/linkedRecords?id=${recordId}`)
}

export const addManyToManyLink = (customFiledId, recordId, company = '{currentCompanySlug}') => {
  return httpClient.post(`/${company}/quotes/customFields/${customFiledId}/linkedRecords`, {
    id: recordId,
  })
}

export const deleteSharedTableRow = (recordId, linkSlug, childObjectSlug, data, company = '{currentCompanySlug}') => {
  return httpClient.delete(`/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/`, {
    data,
  })
}

export const mergeRecords = (baseRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.patch(`/${company}/quotes/${baseRecordId}/merge`, payload)
}

export const calculateFields = (payload) => {
  return httpClient.post(`/{currentCompanySlug}/filter-views/quotes/calculate`, payload)
}

export const restoreRecord = (recordId) => {
  return httpClient.post(`/{currentCompanySlug}/recycle-bin/${recordId}`)
}

export const getVAiFieldConfiguration = (company = '{currentCompanySlug}') => {
  return httpClient.get(
    `${company}/vsimple-ai/field-configurations`,
  ).then(({ data }) => {
    return data
  })
}

export const updateVAiFieldConfiguration = (slug, field) => httpClient.put(
  `{currentCompanySlug}/vsimple-ai/field-configurations/${slug}`,
  field,
)

export const forceProcessVAiFile = (fileId, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/vsimple-ai/files/${fileId}/force-process`,
)

export const forceProcessVAiResource = (resourceId, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/vsimple-ai/resources/${resourceId}/force-process`,
)

export const getFilesHasSummary = (recordId, company = '{currentCompanySlug}') => httpClient.get(
  `${import.meta.env.VITE_AI_BASE_URL}/${company}/records/${recordId}/attachments`,
)

export const getFilesSummaryStatus = (recordId, company = '{currentCompanySlug}') => httpClient.get(
  `${import.meta.env.VITE_AI_BASE_URL}/${company}/records/${recordId}/processing_status`,
)

export const launchCpqIntegration = (recordId, integrationSlug, payload = {}) => httpClient.post(
  `{currentCompanySlug}/records/${recordId}/cpq/${integrationSlug}/quotes`,
  Object.keys(payload).length ? { meta: payload } : undefined,
  {
    skipDefaultForbiddenBehaviour: true,
    ignoreGlobalNotify: true,
  },
)

export const storeCpqIntegrationXmlData = (payload) => httpClient.post(
  `{currentCompanySlug}/cpq/quotes/store-data`,
  payload,
)

export const getCpqIntegrationQuotes = (recordId) => httpClient.get(
  `{currentCompanySlug}/records/${recordId}/cpq/quotes`,
)

export const closeLiftnetQuote = (recordId, quoteId) => httpClient.get(
  `{currentCompanySlug}/records/${recordId}/cpq/liftnet2/quotes/${quoteId}/close-url`,
)

export const postMessageReaction = (id, uuid, payload, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${id}/activity-stream/messages/${uuid}/reactions`,
  payload,
)

export const deleteMessageReaction = (id, uuid, payload, company = '{currentCompanySlug}') => httpClient.delete(
  `${company}/quotes/${id}/activity-stream/messages/${uuid}/reactions`,
  {
    data: payload,
  },
)

export const getRepliedByMessages = (id, params, company = '{currentCompanySlug}') => httpClient.get(
  `${company}/quotes/${id}/activity-stream/groups`, { params })

export const getRecordsByIds = (ids) => httpClient.post(
  '{currentCompanySlug}/quotes/by-ids',
  { ids },
)

export const autocompleteRecords = (data) => httpClient({
  method: 'POST',
  url: `${import.meta.env.VITE_UNIVERSAL_SEARCH_BASE_URL}/{currentCompanySlug}/records/autocomplete`,
  data,
  withCredentials: false,
})

export default {
  getRecords,
  getKanbanRecords,
  getCardRecords,
  createRecord,
  getRecord,
  deleteRecord,
  getRecordActivityStream,
  getRecordFiles,
  getRecordFileGroups,
  createMessage,
  updateMessage,
  readRecordStatuses,
  updateRecordStatus,
  updateRecordType,
  addCollaborators,
  removeCollaborators,
  moveChildRecord,
  moveChildRecordMultiple,
  editRecord,
  exportRecords,
  getFiltersStructure,
  deleteObjectChild,
  editObjectChild,
  createObjectChilds,
  getObjectChildren,
  getObjectChildrenMeta,
  patchObjectField,
  sortObjectFiedlds,
  templateDocumentsList,
  getPersistentColumns,
  savePersistentColumns,
  deleteFileFromRecord,
  restoreRecordFile,
  loadRecordCustomFieldsConfiguration,
  createStatus,
  changeStatusesOrder,
  deleteStatus,
  updateStatus,
  loadFiltersViews,
  createFiltersView,
  deleteFiltersView,
  updateFiltersView,
  reorderFiltersView,
  updateFiltersViewHidden,
  updateFavoriteView,
  getRecordCollaborators,
  patchObjectFieldDryRun,
  loadRecordSections,
  signTemplate,
  loadFiltersViewsCounts,
  getSharedTableData,
  deleteSharedTableRow,
  updateSharedTableData,
  mergeRecords,
  calculateFields,
  createEmbedLinkToSignTemplate,
  restoreRecord,
  getManyToManyLinkData,
  getRecordActivityStreamGroup,
  getVAiFieldConfiguration,
  updateVAiFieldConfiguration,
  forceProcessVAiFile,
  forceProcessVAiResource,
  getFilesHasSummary,
  getFilesSummaryStatus,
  launchCpqIntegration,
  storeCpqIntegrationXmlData,
  getCpqIntegrationQuotes,
  closeLiftnetQuote,
  postMessageReaction,
  deleteMessageReaction,
  getRepliedByMessages,
  loadFiltersViewsForCompany,
  updateFavoriteViewForCompany,
  getRecordsForCompany,
}
